import { join } from 'node:path';
import { isType } from 'variant';
import { outdent } from 'outdent';
import MagicString from 'magic-string';
import { parse } from 'node-html-parser';
import { normalizePath } from 'vite';
import type { PluginOption } from 'vite';
import { generateUrl } from '@wix/cli-app-manifest';
import { ConfigType } from '../vite-config-options.js';
import type { ViteConfigOptions } from '../vite-config-options.js';
import type { ProjectModel } from '../../model.js';
import { DevCenterComponent } from '../../dev-center/components/dev-center-component.js';

type Options = { initialModel: ProjectModel } & ViteConfigOptions;

function getEmbeddedScriptEntries(model: ProjectModel) {
  const entries = model.components
    .filter(isType(DevCenterComponent.EmbeddedScript))
    .map((comp) => normalizePath(join(model.projectFolder, comp.relativePath)));

  return new Set(entries);
}

export function getEmbeddedScriptHtmlUrl(filename: string) {
  return generateUrl(filename);
}

export function embeddedScriptSDKSupport(options: Options): PluginOption {
  const { initialModel, type } = options;
  const query = '?wix-context-internal';
  const virtualModuleId = 'virtual:wix-context-module';
  const resolvedVirtualModuleId = `\0${virtualModuleId}`;
  const virtualScriptId = 'virtual:wix-context-reexport-script';
  const resolvedVirtualScriptId = `\0${virtualScriptId}`;

  let id: string;
  let fileName: string;
  let entriesSet = getEmbeddedScriptEntries(initialModel);

  return [
    {
      name: 'wix:embedded-script-sdk-support-modules',
      enforce: 'pre',
      resolveId(id) {
        if (id === virtualModuleId) {
          return resolvedVirtualModuleId;
        }

        if (id.endsWith(virtualScriptId)) {
          return resolvedVirtualScriptId;
        }

        return null;
      },
      load(id) {
        if (id === resolvedVirtualModuleId) {
          return outdent`
            import { enableClient } from '@wix/cli-app/internal/platform-sdk/site';
            const { injectGetAccessToken } = enableClient('${initialModel.config.appId}');
            export const injectAccessTokenFunction = (getAppToken) => {
              injectGetAccessToken(getAppToken);
            };
          `;
        }

        if (id === resolvedVirtualScriptId) {
          return `export * from '${virtualModuleId}';`;
        }

        return null;
      },
    },
    {
      name: 'wix:embedded-script-sdk-support-serve',
      apply: 'serve',
      enforce: 'pre',
      configureServer() {
        if (type === ConfigType.dev) {
          options.projectModelWatcher.onChange((model) => {
            entriesSet = getEmbeddedScriptEntries(model);
          });
        }
      },
      transform(code, id) {
        if (id.endsWith(query)) {
          const s = new MagicString(code);
          s.prepend(`import '${virtualModuleId}';\n`);

          return {
            code: s.toString(),
            map: s.generateMap(),
          };
        }

        return null;
      },
      transformIndexHtml: {
        order: 'pre',
        handler(html, ctx) {
          if (entriesSet.has(ctx.filename)) {
            const root = parse(html);
            const scripts = root.querySelectorAll('script[type="module"]');

            for (const script of scripts) {
              const src = script.getAttribute('src');
              if (src) {
                script.setAttribute('src', `${src}${query}`);
              } else {
                script.innerHTML = `import '${virtualModuleId}';\n${script.innerHTML}`;
              }
            }

            return {
              tags: [
                {
                  tag: 'script',
                  attrs: {
                    type: 'module',
                    accesstoken: 'true',
                    src: virtualScriptId,
                  },
                },
              ],
              html: root.toString(),
            };
          }

          return html;
        },
      },
    },
    {
      name: 'wix:embedded-script-sdk-support-build-pre',
      apply: 'build',
      enforce: 'pre',
      transformIndexHtml: {
        order: 'pre',
        handler(html, ctx) {
          if (entriesSet.has(ctx.filename)) {
            return {
              tags: [
                {
                  tag: 'script',
                  attrs: {
                    type: 'module',
                    src: virtualModuleId,
                  },
                },
              ],
              html,
            };
          }

          return html;
        },
      },
    },
    {
      name: 'wix:embedded-script-sdk-support-build-post',
      apply: 'build',
      enforce: 'pre',
      buildStart() {
        this.emitFile({
          type: 'chunk',
          id: virtualModuleId,
          preserveSignature: 'exports-only',
        });
        id = this.emitFile({
          type: 'chunk',
          id: virtualScriptId,
          preserveSignature: 'exports-only',
        });
      },
      generateBundle() {
        fileName = this.getFileName(id);
      },
      transformIndexHtml(html, ctx) {
        if (entriesSet.has(ctx.filename)) {
          return {
            tags: [
              {
                tag: 'script',
                attrs: {
                  type: 'module',
                  crossorigin: true,
                  accesstoken: 'true',
                  src: getEmbeddedScriptHtmlUrl(fileName),
                },
              },
            ],
            html,
          };
        }

        return html;
      },
    },
  ];
}
